import React from 'react';
import Badge from 'housecall-ui/dist/DesignSystem/DataDisplay/Badges/Badge';
import Divider from 'housecall-ui/dist/DesignSystem/DataDisplay/Divider/Divider';
import List from 'housecall-ui/dist/DesignSystem/DataDisplay/List/List';
import ListItem from 'housecall-ui/dist/DesignSystem/DataDisplay/List/ListItem';
import Tooltip from 'housecall-ui/dist/DesignSystem/DataDisplay/Tooltips/Tooltip';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import IconButton from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/IconButton';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import Drawer from 'housecall-ui/dist/DesignSystem/Navigation/Drawers/Drawer';
import DrawerHeader from 'housecall-ui/dist/DesignSystem/Navigation/Drawers/DrawerHeader';
import Tab from 'housecall-ui/dist/DesignSystem/Navigation/Tabs/Tab';
import Tabs from 'housecall-ui/dist/DesignSystem/Navigation/Tabs/Tabs';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import useMediaQuery from 'housecall-ui/dist/DesignSystem/Styles/useMediaQuery';
import useTheme from 'housecall-ui/dist/DesignSystem/Styles/useTheme';
import AppStore from 'housecall-ui/dist/HousecallComponents/Apps/DownloadApps/AppStore';
import PlayStore from 'housecall-ui/dist/HousecallComponents/Apps/DownloadApps/PlayStore';
import CloseIcon from 'housecall-ui/dist/IconsAndImages/Icons/Close';
import PropTypes from 'prop-types';

import ProAvatar from 'components/ProAvatar.js';

import AvatarShortcutStates from './AvatarShortcutStates';
import { DrawerFooter } from './DrawerFooter';
import NavbarShortcutStates from './navbarShortcutStates';

const useStyles = makeStyles({
  avatarAccount: {
    paddingLeft: 16,
    paddingBottom: 16,
  },
  infoAccount: {
    maxWidth: 232,
  },
});
function AvatarDrawer({
  open,
  onClose,
  activeTab,
  showCompanySetup,
  toggleActivityFeedDrawer,
  toggleTasksDrawer,
  onMyAppsClick,
  quickbooksBadgeData,
  pro,
  packageLogo,
}) {
  const { avatarAccount, infoAccount } = useStyles();
  const shortcuts = AvatarShortcutStates({ toggleActivityFeedDrawer, toggleTasksDrawer });
  const navbarShortcuts = NavbarShortcutStates({ quickbooksBadgeData, showCompanySetup, onMyAppsClick });
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));

  const proInfo = (
    <Grid container spacing={2} className={avatarAccount}>
      <Grid item>
        <ProAvatar
          backgroundColor={`#${pro.color_hex}`}
          avatar={pro.avatar_url}
          has_avatar={pro.has_avatar}
          initials={pro.initials}
          useLatestMaterialUI
          size={48}
        />
      </Grid>
      <Grid item className={infoAccount}>
        <Typography variant="body1" noWrap>
          {pro.full_name}
        </Typography>
        <Tooltip title={pro.email}>
          <Typography variant="body1" noWrap>
            {pro.email}
          </Typography>
        </Tooltip>
      </Grid>
    </Grid>
  );

  const navbarShortcutTabs = (
    <>
      <Box pb={1}>
        <Tabs orientation="vertical" tabSize="med" value={activeTab || false} version="v2">
          {navbarShortcuts
            .filter((item) => item.show)
            .map((item, index) => (
              <Tab
                key={index}
                version="v2"
                label={<Typography variant="h3">{item.title}</Typography>}
                secondary={<Badge color="info" badgeContent={item.badgeCount} version="v2" />}
                value={item.value}
                href={item.href}
                onClick={item.onClick}
              />
            ))}
        </Tabs>
      </Box>
      <Divider />
    </>
  );

  const menuShortcuts = (
    <List component="nav">
      {shortcuts.map((item, index) => (
        <ListItem alignItems="center" button component="a" key={index} href={item.href} onClick={item.onClick}>
          <Typography variant="h3" color="textPrimary">
            {item.title}
          </Typography>
        </ListItem>
      ))}
    </List>
  );

  const buildFooter = () => {
    if (sm) {
      return (
        <Grid container spacing={5} justify="flex-end" alignItems="flex-end">
          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <AppStore fill={theme.palette.secondary.main} />
              </Grid>
              <Grid item xs={12}>
                <PlayStore fill={theme.palette.secondary.main} />
              </Grid>
            </Grid>
          </Grid>
          {packageLogo && (
            <Grid item>
              <Grid container justify="space-between" alignItems="flex-end">
                <Grid item>{packageLogo}</Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      );
    }
    if (packageLogo) {
      return (
        <Grid container justify="flex-end" alignItems="flex-end">
          <Grid item>{packageLogo}</Grid>
        </Grid>
      );
    }

    return null;
  };

  const footer = buildFooter();

  return (
    <Drawer open={open} onClose={onClose} version="v2" anchor="right" style={{ overflow: 'hidden' }}>
      <DrawerHeader>
        <Grid container justify="flex-end">
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </DrawerHeader>
      {proInfo}
      <Divider />
      {mdDown && navbarShortcutTabs}
      {menuShortcuts}
      {footer && <DrawerFooter>{footer}</DrawerFooter>}
    </Drawer>
  );
}

AvatarDrawer.propTypes = {
  showCompanySetup: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activeTab: PropTypes.string.isRequired,
  toggleActivityFeedDrawer: PropTypes.func.isRequired,
  toggleTasksDrawer: PropTypes.func.isRequired,
  onMyAppsClick: PropTypes.func.isRequired,
  quickbooksBadgeData: PropTypes.shape({
    qboErrorCount: PropTypes.number,
    qbdErrorCount: PropTypes.number,
    qboEnabled: PropTypes.bool,
    qbdEnabled: PropTypes.bool,
  }),
  pro: PropTypes.shape({
    color_hex: PropTypes.string.isRequired,
    avatar_url: PropTypes.string.isRequired,
    full_name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    has_avatar: PropTypes.bool.isRequired,
    initials: PropTypes.string.isRequired,
  }),
  packageLogo: PropTypes.element,
};

export default AvatarDrawer;
